import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  FormControlLabel,
  Paper,
  Skeleton,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { numFont } from '../../../utils/fonts';
import { formatPriceWithCurrency } from '../../../utils/vat';
import ExportButtons from '../components/ExportButtons';
import { useReportData } from '../hooks/useReportsApi';

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return formatPriceWithCurrency(value);
};

const SUBTABS = ['byVendor', 'byCategory', 'due', 'salesVsPurchases'];
// /api/reports/<endpoint>/ ; exports at /api/reports/<endpoint>/export/?export_format=xlsx
const ENDPOINTS = {
  byVendor: 'purchases/by-vendor',
  byCategory: 'purchases/by-category',
  due: 'purchases/due',
  salesVsPurchases: 'purchases/sales-vs-purchases',
};

function Loading() {
  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Skeleton variant="text" width={240} />
      <Skeleton variant="rectangular" height={160} sx={{ mt: 2 }} />
    </Paper>
  );
}

function ByVendor({ t, data }) {
  const rows = data?.rows || [];
  const totals = data?.totals || {};
  return (
    <TableContainer component={Paper} elevation={0}>
      <Table size="small" data-testid="purchases-by-vendor-table">
        <TableHead>
          <TableRow>
            <TableCell>{t('purchaseLedger.fields.vendor')}</TableCell>
            <TableCell align="right">{t('purchaseLedger.reports.count')}</TableCell>
            <TableCell align="right">{t('purchaseLedger.fields.totalNet')}</TableCell>
            <TableCell align="right">{t('purchaseLedger.fields.totalVat')}</TableCell>
            <TableCell align="right">{t('purchaseLedger.fields.totalGross')}</TableCell>
            <TableCell align="right">{t('purchaseLedger.reports.open')}</TableCell>
            <TableCell align="right">{t('purchaseLedger.summary.overdue')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography color="text.secondary">{t('purchaseLedger.reports.noData')}</Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.vendor_id ?? row.vendor_name}>
              <TableCell>{row.vendor_name || t('purchaseLedger.vendor.unknown')}</TableCell>
              <TableCell align="right" sx={numFont}>{row.count ?? 0}</TableCell>
              <TableCell align="right" sx={numFont}>{formatCurrency(row.net)}</TableCell>
              <TableCell align="right" sx={numFont}>{formatCurrency(row.vat)}</TableCell>
              <TableCell align="right" sx={{ ...numFont, fontWeight: 600 }}>{formatCurrency(row.gross)}</TableCell>
              <TableCell align="right" sx={numFont}>{formatCurrency(row.open)}</TableCell>
              <TableCell align="right" sx={{ ...numFont, color: parseFloat(row.overdue || 0) > 0 ? 'error.main' : 'inherit' }}>{formatCurrency(row.overdue)}</TableCell>
            </TableRow>
          ))}
          {rows.length > 0 && (
            <TableRow sx={{ '& td': { fontWeight: 700, borderTop: 2, borderColor: 'divider' } }}>
              <TableCell>{t('purchaseLedger.reports.total')}</TableCell>
              <TableCell align="right" sx={numFont}>{totals.count ?? rows.reduce((s, r) => s + (r.count || 0), 0)}</TableCell>
              <TableCell align="right" sx={numFont}>{formatCurrency(totals.net)}</TableCell>
              <TableCell align="right" sx={numFont}>{formatCurrency(totals.vat)}</TableCell>
              <TableCell align="right" sx={numFont}>{formatCurrency(totals.gross)}</TableCell>
              <TableCell align="right" sx={numFont}>{formatCurrency(totals.open)}</TableCell>
              <TableCell align="right" sx={numFont}>{formatCurrency(totals.overdue)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ByCategory({ t, data }) {
  const months = data?.months || [];
  const rows = data?.rows || [];
  return (
    <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
      <Table size="small" data-testid="purchases-by-category-table">
        <TableHead>
          <TableRow>
            <TableCell>{t('purchaseLedger.fields.costCategory')}</TableCell>
            {months.map((m) => (
              <TableCell key={m} align="right" sx={numFont}>{m}</TableCell>
            ))}
            <TableCell align="right">{t('purchaseLedger.reports.total')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={months.length + 2}>
                <Typography color="text.secondary">{t('purchaseLedger.reports.noData')}</Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={row.category_id ?? row.category_name}>
              <TableCell>{row.category_name || t('purchaseLedger.fields.noCategory')}</TableCell>
              {months.map((m) => (
                <TableCell key={m} align="right" sx={numFont}>{formatCurrency(row.by_month?.[m])}</TableCell>
              ))}
              <TableCell align="right" sx={{ ...numFont, fontWeight: 600 }}>{formatCurrency(row.total_net)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Due({ t, data }) {
  const buckets = data?.buckets || [];
  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 700 }}>{t('purchaseLedger.summary.payableNow')}</Typography>
        <Typography variant="h5" sx={{ ...numFont, fontWeight: 700 }} data-testid="purchases-payable-now">{formatCurrency(data?.payable_now)}</Typography>
      </Paper>
      <TableContainer component={Paper} elevation={0}>
        <Table size="small" data-testid="purchases-due-table">
          <TableHead>
            <TableRow>
              <TableCell>{t('purchaseLedger.reports.bucket')}</TableCell>
              <TableCell align="right">{t('purchaseLedger.reports.count')}</TableCell>
              <TableCell align="right">{t('purchaseLedger.fields.totalGross')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buckets.map((b) => (
              <TableRow key={b.key} sx={b.key === 'overdue' ? { '& td': { color: 'error.main', fontWeight: 600 } } : {}}>
                <TableCell>{t(`purchaseLedger.reports.buckets.${b.key}`, { defaultValue: b.key })}</TableCell>
                <TableCell align="right" sx={numFont}>{b.count ?? 0}</TableCell>
                <TableCell align="right" sx={numFont}>{formatCurrency(b.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function SalesVsPurchases({ t, data }) {
  const rows = data?.rows || [];
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>{t('purchaseLedger.reports.salesVsPurchasesDisclaimer')}</Alert>
      <TableContainer component={Paper} elevation={0}>
        <Table size="small" data-testid="sales-vs-purchases-table">
          <TableHead>
            <TableRow>
              <TableCell>{t('purchaseLedger.reports.month')}</TableCell>
              <TableCell align="right">{t('purchaseLedger.reports.salesNet')}</TableCell>
              <TableCell align="right">{t('purchaseLedger.reports.purchasesNet')}</TableCell>
              <TableCell align="right">{t('purchaseLedger.reports.difference')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary">{t('purchaseLedger.reports.noData')}</Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.month}>
                <TableCell sx={numFont}>{row.month}</TableCell>
                <TableCell align="right" sx={numFont}>{formatCurrency(row.sales_net)}</TableCell>
                <TableCell align="right" sx={numFont}>{formatCurrency(row.purchases_net)}</TableCell>
                <TableCell align="right" sx={{ ...numFont, fontWeight: 600, color: parseFloat(row.difference || 0) < 0 ? 'error.main' : 'inherit' }}>{formatCurrency(row.difference)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function PurchasesTab({ t, dateParams }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('subtab');
  const [subTab, setSubTab] = useState(SUBTABS.includes(requested) ? requested : 'byVendor');
  const [receiptsOnly, setReceiptsOnly] = useState(false);

  useEffect(() => {
    const param = searchParams.get('subtab');
    if (param && SUBTABS.includes(param) && param !== subTab) setSubTab(param);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const params = subTab === 'byCategory' && receiptsOnly ? { ...dateParams, kind: 'receipt' } : dateParams;
  const { data, loading, error } = useReportData(ENDPOINTS[subTab], params);

  const handleSubTabChange = (event, value) => {
    setSubTab(value);
    setSearchParams({ tab: 'purchases', subtab: value }, { replace: true });
  };

  const exportable = subTab === 'byVendor' || subTab === 'byCategory';

  return (
    <Box data-testid="purchases-tab">
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Tabs value={subTab} onChange={handleSubTabChange} variant="scrollable" scrollButtons="auto" sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minWidth: 'auto', px: 2 } }}>
          {SUBTABS.map((key) => (
            <Tab key={key} value={key} label={t(`purchaseLedger.reports.tabs.${key}`)} data-testid={`purchases-subtab-${key}`} />
          ))}
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {subTab === 'byCategory' && (
            <FormControlLabel control={<Switch size="small" checked={receiptsOnly} onChange={(e) => setReceiptsOnly(e.target.checked)} />} label={t('purchaseLedger.reports.receiptsOnly')} />
          )}
          {exportable && <ExportButtons reportType={ENDPOINTS[subTab]} params={params} testIdPrefix={`purchases-export-${subTab}`} excelOnly />}
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{t('reports.error')}: {error}</Alert>}
      {loading && <Loading />}
      {!loading && subTab === 'byVendor' && <ByVendor t={t} data={data} />}
      {!loading && subTab === 'byCategory' && <ByCategory t={t} data={data} />}
      {!loading && subTab === 'due' && <Due t={t} data={data} />}
      {!loading && subTab === 'salesVsPurchases' && <SalesVsPurchases t={t} data={data} />}
    </Box>
  );
}

export default withTranslation()(PurchasesTab);
